import classNames from "classnames"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    dataSelectorsByMode,
    SidePanelMode,
    useMultipleSelectedContextMenuActionsByMode,
} from "../constants"
import { useSidePanelDragAndDrop } from "../dragAndDrop"
import {
    addLayerToSelection,
    clearSelection,
    removeLayerFromSelection,
    selectLayerRange,
    selectRenamingLayerId,
    selectSelectedLayerIds,
    selectSingleLayer,
    setRenamingLayerId,
} from "../slice.new"
import {
    getLocalStorageValue,
    setLocalStorageValue,
    useLocalStorageValue,
} from "tools/store/localStorageRegistry"
import { FlattenedSidePanelLayersData } from "../useSidePanelLayers"
import { FlattenedSidePanelPdfsData } from "../useSidePanelPdfs"
import { SidePanelLayerOrGroup, SidePanelLayerType } from "@nova/constants"
import { selectEntityById } from "../slice"
import { SidePanelStickyLayersData } from "../useSidePanelStickyLayers"
import * as Icons from "@nova/component-icons"
import { createPortal } from "react-dom"
import store from "integrations/redux/store"
import { getSidePanelLayerTypeToUpdateFunctionMap } from "plugins/plugin-registry"
import { useIsEditor } from "tools/hooks/useIsEditor"

function useIsSelected(id: string) {
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const isSelected = useMemo(() => selectedLayerIds.includes(id), [id, selectedLayerIds])
    return isSelected
}

function useIsLayerExpanded(id: string) {
    const expandedLayerIds = useLocalStorageValue("sidePanelExpandedLayerIds")
    return expandedLayerIds.includes(id)
}

type AncestorHandlerHook<T extends "hover" | "select"> = (
    p: {
        ancestors: { id: string; isLastItemInParent: boolean }[]
        isLastItemInParent: boolean
        hasChildren: boolean
    } & (T extends "hover" ? { hoveredItemId: string | undefined } : unknown)
) => T extends "hover"
    ? {
          isAncestorHovered: boolean
          ancestorHoveredDepth: number
          isLastItemInAncestorHover: boolean
      }
    : {
          isAncestorSelected: boolean
          ancestorSelectedDepth: number
          isLastItemInAncestorSelect: boolean
      }

const useAncestorHover: AncestorHandlerHook<"hover"> = ({
    ancestors,
    isLastItemInParent,
    hoveredItemId,
    hasChildren,
}) => {
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const isAncestorHovered = useMemo(() => {
        if (!hoveredItemId || selectedLayerIds.includes(hoveredItemId)) return false
        return !!ancestors.find((ancestor) => ancestor.id === hoveredItemId)
    }, [ancestors, hoveredItemId, selectedLayerIds])
    const ancestorHoveredDepth = useMemo(() => {
        if (!isAncestorHovered) return 0
        return ancestors.length - ancestors.findIndex((ancestor) => ancestor.id === hoveredItemId)
    }, [ancestors, hoveredItemId, isAncestorHovered])
    const isLastItemInAncestorHover = useMemo(() => {
        if (!isAncestorHovered || !isLastItemInParent || hasChildren) return false
        for (let i = ancestors.length - 1; i >= 0; i--) {
            if (ancestors[i].id === hoveredItemId) return true
            if (!ancestors[i].isLastItemInParent) return false
        }
        return false
    }, [ancestors, hasChildren, hoveredItemId, isAncestorHovered, isLastItemInParent])
    return {
        isAncestorHovered,
        ancestorHoveredDepth,
        isLastItemInAncestorHover,
    }
}

const useAncestorSelect: AncestorHandlerHook<"select"> = ({
    ancestors,
    isLastItemInParent,
    hasChildren,
}) => {
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const isAncestorSelected = useMemo(() => {
        return ancestors.some((ancestor) => selectedLayerIds.includes(ancestor.id))
    }, [ancestors, selectedLayerIds])
    const ancestorSelectedDepth = useMemo(() => {
        if (!isAncestorSelected) return 0
        return (
            ancestors.length -
            ancestors.findIndex((ancestor) => selectedLayerIds.includes(ancestor.id))
        )
    }, [ancestors, isAncestorSelected, selectedLayerIds])
    const isLastItemInAncestorSelect = useMemo(() => {
        if (!isAncestorSelected || !isLastItemInParent || hasChildren) return false
        for (let i = ancestors.length - 1; i >= 0; i--) {
            if (selectedLayerIds.includes(ancestors[i].id)) return true
            if (!ancestors[i].isLastItemInParent) return false
        }
        return false
    }, [isAncestorSelected, isLastItemInParent, hasChildren, ancestors, selectedLayerIds])
    return {
        isAncestorSelected,
        ancestorSelectedDepth,
        isLastItemInAncestorSelect,
    }
}

export function getGenericListItemWrapper({ mode }: { mode: SidePanelMode }) {
    const GenericListItemWrapper = memo(function GenericListItemWrapper<
        T extends FlattenedSidePanelLayersData | FlattenedSidePanelPdfsData,
    >({
        item,
        hoveredItemId,
        sortedEntities,
    }: {
        item: T
        hoveredItemId: string | undefined
        sortedEntities: SidePanelLayerOrGroup[]
    }) {
        const dispatch = useAppDispatch()
        const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
        const contextMenuIsForThisItem =
            activeSidePanelContextMenu?.data.layerId === item.id &&
            activeSidePanelContextMenu.data.selectedLayerIds.length === 1
        const contextMenuIsForMultipleIncludingThisItem =
            activeSidePanelContextMenu?.data.layerId === item.id &&
            activeSidePanelContextMenu.data.selectedLayerIds.length > 1
        const isSelected = useIsSelected(item.id)
        const isLayerExpanded = useIsLayerExpanded(item.id)
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === item.id

        const entityMap = useAppSelector(dataSelectorsByMode[mode].selectEntityMap)
        const entity = useAppSelector(selectEntityById(item.id))
        const isGroup = entity?.type === SidePanelLayerType.Group

        const isEditor = useIsEditor()

        const onClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation()
                e.preventDefault()
                if (activeSidePanelContextMenu) {
                    dispatch(setActiveContextMenu(undefined))
                    return
                }
                if (renamingLayerId && !isRenaming) {
                    dispatch(setRenamingLayerId({ id: undefined }))
                }
                // Select layer(s)
                const { id } = item
                if (e.shiftKey) {
                    document.getSelection()?.removeAllRanges() // Prevent selecting text
                    dispatch(selectLayerRange({ id, dataFlatMap: entityMap, sortedEntities }))
                } else if (e.metaKey || e.ctrlKey) {
                    if (isSelected) {
                        dispatch(removeLayerFromSelection({ id }))
                    } else {
                        dispatch(addLayerToSelection({ id }))
                    }
                } else {
                    dispatch(selectSingleLayer({ id }))
                }
                // Expand/collapse children
                if (!e.shiftKey && !e.metaKey && !e.ctrlKey && isGroup) {
                    const expandedLayerIds = getLocalStorageValue("sidePanelExpandedLayerIds")
                    if (isLayerExpanded) {
                        setLocalStorageValue(
                            "sidePanelExpandedLayerIds",
                            expandedLayerIds.filter((layerId) => layerId !== item.id)
                        )
                    } else if (!expandedLayerIds.includes(item.id)) {
                        setLocalStorageValue("sidePanelExpandedLayerIds", [
                            ...expandedLayerIds,
                            item.id,
                        ])
                    }
                }
            },
            [
                activeSidePanelContextMenu,
                dispatch,
                entityMap,
                isGroup,
                isLayerExpanded,
                isRenaming,
                isSelected,
                item,
                renamingLayerId,
                sortedEntities,
            ]
        )

        const onContextMenu = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (activeSidePanelContextMenu) {
                    dispatch(setActiveContextMenu(undefined))
                    return
                }
                const state = store.getState()
                const selectedLayerIds = selectSelectedLayerIds(state)
                if (!isEditor && isSelected && selectedLayerIds.length > 1) return
                dispatch(
                    setActiveContextMenu({
                        type: "SidePanelItem",
                        data: {
                            layerId: item.id,
                            selectedLayerIds: isSelected ? selectedLayerIds : [item.id],
                        },
                        position: { left: e.clientX, top: e.clientY },
                    })
                )
            },
            [activeSidePanelContextMenu, dispatch, isEditor, isSelected, item.id]
        )

        const contextMenuSections = item.plugin.useContextMenuSections(item.id, sortedEntities)
        const multipleSelectedContextMenuSections =
            useMultipleSelectedContextMenuActionsByMode[mode]()

        const {
            dragRef,
            TopDropComponent,
            BottomDropComponent,
            DragPreviewComponent,
            isDragging,
            isOverTop,
            canDropTop,
            isOverBottom,
            canDropBottom,
            anythingIsDragging,
        } = useSidePanelDragAndDrop({
            entityId: item.id,
            mode,
            updateFn: getSidePanelLayerTypeToUpdateFunctionMap()[entity.type!],
            shortenIndicator: isGroup,
            sortedEntities,
        })

        const { isAncestorHovered, ancestorHoveredDepth, isLastItemInAncestorHover } =
            useAncestorHover({
                ancestors: item.ancestors,
                isLastItemInParent: item.isLastItemInParent,
                hasChildren: isLayerExpanded && item.directChildrenCount > 0,
                hoveredItemId,
            })
        const { isAncestorSelected, ancestorSelectedDepth, isLastItemInAncestorSelect } =
            useAncestorSelect({
                ancestors: item.ancestors,
                isLastItemInParent: item.isLastItemInParent,
                hasChildren: isLayerExpanded && item.directChildrenCount > 0,
            })

        const itemDragRef = useRef<HTMLDivElement>(null)
        dragRef(itemDragRef)

        return (
            <div
                className={classNames("w-full select-none relative py-px", {
                    "opacity-50": isDragging,
                })}
                style={{
                    paddingLeft: `${item.depth * 24}px`,
                }}
            >
                {/* Background when an ancestor is selected (faint blue) */}
                <div
                    className={classNames("absolute top-0 right-0 bottom-0", {
                        "bg-[#0F1E27]": isAncestorSelected,
                        "rounded-b-lg":
                            isLastItemInAncestorSelect &&
                            (!isLayerExpanded || !item.directChildrenCount),
                    })}
                    style={{
                        left: `${(item.depth - ancestorSelectedDepth) * 24}px`,
                    }}
                />
                {/* Background when an ancestor is hovered (faint gray) */}
                <div
                    className={classNames("absolute top-0 right-0 bottom-0", {
                        "bg-white-100": isAncestorHovered,
                        "rounded-b-lg":
                            isLastItemInAncestorHover &&
                            (!isLayerExpanded || !item.directChildrenCount),
                    })}
                    style={{
                        left: `${(item.depth - ancestorHoveredDepth) * 24}px`,
                    }}
                />
                <div
                    ref={itemDragRef}
                    onClick={onClick}
                    onContextMenu={(e) => {
                        if (e.metaKey || e.ctrlKey) onClick(e)
                        if (e.button !== 2) return
                        onContextMenu(e)
                    }}
                    className={classNames(
                        "w-full cursor-pointer relative min-h-[32px] flex items-center py-px",
                        {
                            "rounded-lg": !isLayerExpanded,
                            "rounded-t-lg": isLayerExpanded,
                            "rounded-br-lg": isLayerExpanded && !item.directChildrenCount,
                            "bg-blue-700": isSelected && !isRenaming,
                            "bg-white-200": hoveredItemId === item.id && !isSelected && !isRenaming,
                            "outline outline-blue-500": isRenaming,
                        }
                    )}
                >
                    <DragPreviewComponent />
                    <TopDropComponent
                        isDragging={isDragging}
                        isOver={isOverTop}
                        canDrop={canDropTop}
                        anythingIsDragging={anythingIsDragging}
                    />
                    <item.plugin.SidePanelListItem
                        id={item.id}
                        isHovered={hoveredItemId === item.id}
                    />
                    <BottomDropComponent
                        isDragging={isDragging}
                        isOver={isOverBottom}
                        canDrop={canDropBottom}
                        anythingIsDragging={anythingIsDragging}
                    />
                    <MultipleSelectedOptionsPopupButton
                        id={item.id}
                        position={item.isLastItemInParent ? "above" : "below"}
                    />
                    {contextMenuIsForThisItem &&
                        createPortal(
                            <ContextMenu
                                sections={contextMenuSections}
                                onBlur={() => dispatch(setActiveContextMenu(undefined))}
                                isFixedPosition={true}
                                top={activeSidePanelContextMenu.position.top}
                                left={activeSidePanelContextMenu.position.left}
                            />,
                            document.getElementById("portal")!
                        )}
                    {contextMenuIsForMultipleIncludingThisItem &&
                        createPortal(
                            <ContextMenu
                                sections={multipleSelectedContextMenuSections}
                                onBlur={() => dispatch(setActiveContextMenu(undefined))}
                                isFixedPosition={true}
                                top={activeSidePanelContextMenu.position.top}
                                left={activeSidePanelContextMenu.position.left}
                            />,
                            document.getElementById("portal")!
                        )}
                </div>
            </div>
        )
    })
    return GenericListItemWrapper
}

function MultipleSelectedOptionsPopupButton({
    id,
    position,
}: {
    id: string
    position: "above" | "below"
}) {
    const dispatch = useAppDispatch()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
    const contextMenuIsForMultipleIncludingThisItem =
        activeSidePanelContextMenu?.data.layerId === id &&
        activeSidePanelContextMenu.data.selectedLayerIds.length > 1
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const fullyInView = rect.top >= 0 && rect.bottom <= window.innerHeight
        if (!fullyInView) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
    }, [])
    const onClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            if (activeSidePanelContextMenu) {
                dispatch(setActiveContextMenu(undefined))
                return
            }
            const el = ref.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            dispatch(
                setActiveContextMenu({
                    type: "SidePanelItem",
                    data: {
                        layerId: id,
                        selectedLayerIds,
                    },
                    position: { left: rect.left - 64, top: rect.top },
                })
            )
        },
        [activeSidePanelContextMenu, dispatch, id, selectedLayerIds]
    )
    const isEditor = useIsEditor()
    if (!isEditor || selectedLayerIds.length <= 1 || selectedLayerIds[0] !== id) return null
    return (
        <div
            ref={ref}
            className={classNames(
                "absolute right-0 border rounded-lg flex justify-center items-center gap-0.5",
                "px-2 py-1",
                "transition-all duration-50",
                {
                    "-top-px -translate-y-full": position === "above",
                    "-bottom-px translate-y-full": position === "below",
                },
                {
                    "bg-gray-700 border-white-200 hover:bg-blue-700 hover:border-white-100":
                        !contextMenuIsForMultipleIncludingThisItem,
                    "bg-blue-700 border-white-100": contextMenuIsForMultipleIncludingThisItem,
                }
            )}
            style={{
                boxShadow: "0 -1px 3px 0 rgba(0, 0, 0, 0.25), 0 3px 12px 0 rgba(0, 0, 0, 0.45)",
            }}
            onClick={onClick}
        >
            <small>Options</small>
            <Icons.ExtraSmallIcon Icon={Icons.ArrowDropdown} />
        </div>
    )
}

export function getStickyEntityListItemWrapper() {
    const GenericListItemWrapper = memo(function GenericListItemWrapper<
        T extends SidePanelStickyLayersData,
    >({ item, sortedEntities }: { item: T; sortedEntities: SidePanelLayerOrGroup[] }) {
        const dispatch = useAppDispatch()
        const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
        const contextMenuIsForThisItem =
            activeSidePanelContextMenu?.data.layerId === item.id &&
            activeSidePanelContextMenu.data.selectedLayerIds.length === 1
        const [isHovered, setIsHovered] = useState(false)
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === item.id

        const onClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation()
                if (activeSidePanelContextMenu) {
                    dispatch(setActiveContextMenu(undefined))
                    return
                }
                if (renamingLayerId && !isRenaming) {
                    dispatch(setRenamingLayerId({ id: undefined }))
                }
                dispatch(clearSelection())
            },
            [activeSidePanelContextMenu, dispatch, isRenaming, renamingLayerId]
        )

        const onContextMenu = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (activeSidePanelContextMenu) {
                    dispatch(setActiveContextMenu(undefined))
                    return
                }
                dispatch(clearSelection())
                dispatch(
                    setActiveContextMenu({
                        type: "SidePanelItem",
                        data: {
                            layerId: item.id,
                            selectedLayerIds: [item.id],
                        },
                        position: { left: e.clientX, top: e.clientY },
                    })
                )
            },
            [activeSidePanelContextMenu, dispatch, item.id]
        )

        const contextMenuSections = item.plugin.useContextMenuSections(item.id, sortedEntities)

        return (
            <>
                <div
                    className={classNames("w-full mb-[2px] select-none rounded-lg", {
                        "bg-white-100": isHovered,
                    })}
                >
                    <div
                        onClick={onClick}
                        onContextMenu={(e) => {
                            if (e.metaKey || e.ctrlKey) onClick(e)
                            if (e.button !== 2) return
                            onContextMenu(e)
                        }}
                        className={classNames(
                            "w-full cursor-pointer relative min-h-[32px] flex items-center rounded-lg",
                            {
                                "bg-white-100": isHovered && !isRenaming,
                                "outline outline-blue-500": isRenaming,
                            }
                        )}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <item.plugin.SidePanelListItem id={item.id} isHovered={isHovered} />
                    </div>
                    {contextMenuIsForThisItem &&
                        createPortal(
                            <ContextMenu
                                sections={contextMenuSections}
                                onBlur={() => dispatch(setActiveContextMenu(undefined))}
                                isFixedPosition={true}
                                top={activeSidePanelContextMenu.position.top}
                                left={activeSidePanelContextMenu.position.left}
                            />,
                            document.getElementById("portal")!
                        )}
                </div>
            </>
        )
    })
    return GenericListItemWrapper
}
